import { toast } from '@/store/toast';
import { logoutWithReason } from '@/lib/auth-events';

/**
 * Shared handler for the `requires_relogin: true` signal.
 *
 * Today only POST /auth/join-building (Session 8.b) returns this
 * field — when a user switches tenants their JWT's tenant_id
 * claim is stale until expiry. The server has already cleared
 * the refresh cookie; we drop local auth state, surface a toast,
 * and redirect to /login.
 *
 * This lives in its own module so future endpoints that change
 * tenant or otherwise invalidate session state can call the same
 * plumbing without per-component duplication. Add new endpoints
 * by checking the response's `requires_relogin` and calling
 * this helper.
 */
export function handleRequiresRelogin(
  navigate: (path: string) => void,
  opts?: {
    tenantName?: string;
    /** Pre-translated toast copy. Callers that own a t() (leave-
     *  building) pass it so the bounce reads in the driver's locale
     *  and says what actually happened; the two literals below are
     *  the join-building fallbacks. */
    message?: string;
  },
): void {
  // Drop persisted auth state so the next render of <App> sees
  // isAuthenticated=false and lands on /login. The server already
  // cleared the refresh cookie; clearing local state is the
  // companion side of the handshake. Routed through
  // logoutWithReason so the event trail records WHY the user got
  // bounced (tenant-switch flows look identical to a refresh
  // failure from the user's POV; the reason tag disambiguates).
  logoutWithReason('manual-logout', {
    trigger: 'requires-relogin',
    tenantName: opts?.tenantName ?? null,
  });

  if (opts?.message) {
    toast(opts.message, { tone: 'accent', durationMs: 4000 });
  } else if (opts?.tenantName) {
    toast(`Joined ${opts.tenantName}. Log in to continue.`, { tone: 'accent', durationMs: 4000 });
  } else {
    toast('Building changed. Log in to continue.', { tone: 'accent', durationMs: 4000 });
  }

  // Route to /login. We use `navigate` so the App's pushState +
  // setPath flow stays consistent; the App's public-route fallback
  // will render LoginPage.
  navigate('/login');
}
